/**
 * Transform flat `sandbox_provision_timings` rows into a waterfall/Gantt model.
 *
 * The backend stores a 3-level hierarchy flat (a `total` + `phase4_deploy`
 * phase, `deploy_step` children, and `container` build rows that run *inside*
 * `app_up`), each stamped with a wall-clock `startedAt`. Here we anchor every
 * row to the run's start so the chart can position bars by real elapsed time —
 * which is what reveals the parallelism (container builds overlapping inside
 * "Start app"). Container builds become `subEntries` of the "Start app" step so
 * their concurrency is visible without flattening the hierarchy.
 *
 * Returns `null` when there are no `startedAt` anchors (older runs recorded
 * before that column existed) — the caller falls back to the stacked-bar
 * breakdown, which only needs durations.
 *
 * @module features/sandboxes/components/overview/provisionTimingWaterfall
 */

import { NON_LEAF_PHASE_STEPS } from '@chatsdet/types';
import type { WaterfallEntry, WaterfallGroup, WaterfallSubEntry } from '@chatsdet/ui';
import type { ProvisionTiming } from '../../hooks/useProvisionTimings';
import {
  cacheRestoreTone,
  describeTiming,
  OTHER_SETUP_META,
  provisionPhaseGroups,
  timingPhaseOf,
} from './provisionTimingMeta';

export interface ProvisionWaterfall {
  entries: WaterfallEntry[];
  totalDurationMs: number;
  /** Rows expanded on first render (the "Start app" step, to show its builds). */
  defaultExpandedIds: string[];
  /**
   * The Ready / Build phase groups the entries are bucketed into (via each
   * entry's `groupId`), so the chart shows an explicit break between "sandbox
   * ready" and "built". See {@link provisionPhaseGroups}.
   */
  groups: WaterfallGroup[];
}

const startMsOf = (t: ProvisionTiming): number | null =>
  t.startedAt ? new Date(t.startedAt).getTime() : null;

const bySequence = (a: ProvisionTiming, b: ProvisionTiming) => a.sequence - b.sequence;

/**
 * Rows nested under a parent entry, positioned relative to that parent's start.
 *
 * `tone` is usually one tone for the whole group (health stages, the pool
 * claim), but `cache_restore` rows need a per-row tone driven by their outcome
 * (hit/prime vs. miss) — so it also accepts a resolver function.
 */
function nestedSubEntries(
  rows: ProvisionTiming[],
  parentStartMs: number,
  type: WaterfallSubEntry['type'],
  tone: WaterfallSubEntry['tone'] | ((row: ProvisionTiming) => WaterfallSubEntry['tone'])
): WaterfallSubEntry[] {
  const subs: WaterfallSubEntry[] = [];
  for (const row of rows) {
    const startMs = startMsOf(row);
    if (startMs === null) continue; // unanchored — can't be placed on the timeline
    const meta = describeTiming(row.kind, row.step, row.outcome);
    subs.push({
      id: row.id,
      label: meta.name,
      tooltipDetail: meta.description || undefined,
      startMs: startMs - parentStartMs,
      durationMs: row.durationMs,
      tone: typeof tone === 'function' ? tone(row) : tone,
      type,
    });
  }
  return subs;
}

/** Container builds nested under "Start app", positioned relative to its start. */
function buildContainerSubEntries(
  containers: ProvisionTiming[],
  appUpStartMs: number
): WaterfallSubEntry[] {
  const subs: WaterfallSubEntry[] = [];
  for (const c of containers) {
    const startMs = startMsOf(c);
    if (startMs === null) continue; // unanchored build — omit from the timeline
    const meta = describeTiming(c.kind, c.step);
    subs.push({
      id: c.id,
      label: meta.name,
      tooltipDetail: meta.description || undefined,
      startMs: startMs - appUpStartMs,
      durationMs: c.durationMs,
      tone: 'success',
      type: 'container',
    });
  }
  return subs;
}

export function buildProvisionWaterfall(
  timings: ProvisionTiming[],
  options?: {
    /**
     * `<phase> · <step>` label of the step provisioning died in. The failing
     * phase never closes its own timer, so it has no row — it is appended as a
     * synthetic failed bar covering the unaccounted tail. See
     * {@link appendFailedStepEntry}.
     */
    failedStep?: string | null;
  }
): ProvisionWaterfall | null {
  if (!timings || timings.length === 0) return null;

  const total = timings.find((t) => t.kind === 'phase' && t.step === 'total');
  const deploy = timings.find((t) => t.kind === 'phase' && t.step === 'phase4_deploy');
  const deploySteps = timings.filter((t) => t.kind === 'deploy_step').sort(bySequence);
  const containers = timings.filter((t) => t.kind === 'container').sort(bySequence);
  const healthStages = timings.filter((t) => t.kind === 'health_stage').sort(bySequence);
  const cacheRestores = timings.filter((t) => t.kind === 'cache_restore').sort(bySequence);
  const poolClaim = timings.find((t) => t.kind === 'phase' && t.step === 'stopped_pool_claim');
  // Leaf phase rows (recorded since 2026-08-03). When present they tile the whole
  // provision, which makes the "Other setup" / synthetic "Build" estimates below
  // unnecessary — those stay only for runs recorded before phase rows existed.
  const leafPhases = timings
    .filter((t) => t.kind === 'phase' && !NON_LEAF_PHASE_STEPS.has(t.step))
    .sort(bySequence);

  // Timeline origin = the whole-provision start. Without any anchor we can't
  // place bars, so the caller renders the stacked-bar breakdown instead. Prefer
  // the `total` row's start; otherwise the earliest anchor we have.
  const anchoredStarts = timings.map(startMsOf).filter((n): n is number => n !== null);
  if (anchoredStarts.length === 0) return null;
  const originMs = (total ? startMsOf(total) : null) ?? Math.min(...anchoredStarts);

  const offsetOf = (t: ProvisionTiming): number | null => {
    const start = startMsOf(t);
    return start === null ? null : Math.max(0, start - originMs);
  };

  const entries: WaterfallEntry[] = [];
  const defaultExpandedIds: string[] = [];

  const deployOffset = deploy ? offsetOf(deploy) : null;

  // Leading "Other setup" — pre-deploy infra (EC2 launch, SSM, ALB) that had no
  // row of its own, rendered as the gap between the run start and deploy start.
  // Only for runs predating the leaf phase rows, which measure that same span
  // exactly (phase1_allocate / phase2_ssm_ready / phase3_log_group) instead of
  // estimating it as a gap.
  if (leafPhases.length === 0 && deployOffset !== null && deployOffset > 0) {
    entries.push({
      id: 'other-setup',
      label: OTHER_SETUP_META.name,
      startMs: 0,
      durationMs: deployOffset,
      status: 'success',
      tone: 'muted',
      groupId: 'ready',
    });
  }

  // The Deploy phase is no longer a single umbrella bar — its steps split across
  // the Ready and Build groups (headers render the rolled-up spans instead).
  // The deploy steps are the real timeline; container builds nest under app_up.
  // Surface containers as their own top-level steps only when there is no app_up
  // step to nest them under — otherwise nesting owns them. Keying purely on
  // `hasAppUp` makes nesting and top-level mutually exclusive (no double-render)
  // while never silently dropping builds when app_up is absent.
  //
  // Leaf phase rows join the same top-level list: together with the deploy steps
  // (which stand in for phase4_deploy) they tile the provision end to end.
  let workEntryCount = 0;
  const hasAppUp = deploySteps.some((s) => s.step === 'app_up');
  const orderedSteps = [
    ...leafPhases,
    ...(hasAppUp ? deploySteps : [...deploySteps, ...containers]),
  ].sort(bySequence);
  for (const step of orderedSteps) {
    const stepStart = startMsOf(step);
    if (stepStart === null) continue;
    const meta = describeTiming(step.kind, step.step);
    const isAppUp = step.step === 'app_up';
    // Each parent owns exactly one kind of child, so a row can never be charted
    // twice: builds under app_up, health stages under the gate, the pool claim
    // under the allocate phase, and per-service cache-restore outcomes under
    // the cache_restore deploy step.
    const builds =
      isAppUp && containers.length > 0 ? buildContainerSubEntries(containers, stepStart) : [];
    const stages =
      step.step === 'phase7_health'
        ? nestedSubEntries(healthStages, stepStart, 'wait', 'cached')
        : [];
    const claim =
      step.step === 'phase1_allocate' && poolClaim
        ? nestedSubEntries([poolClaim], stepStart, 'action', 'cached')
        : [];
    const restores =
      step.step === 'cache_restore' && cacheRestores.length > 0
        ? nestedSubEntries(cacheRestores, stepStart, 'action', (r) => cacheRestoreTone(r.outcome))
        : [];
    const subEntries = [...builds, ...stages, ...claim, ...restores];
    const badges = builds.length > 0 ? [{ label: `${builds.length} builds`, tone: 'success' as const }] : undefined;
    entries.push({
      id: step.id,
      label: meta.name,
      startMs: Math.max(0, stepStart - originMs),
      durationMs: step.durationMs,
      status: 'success',
      tone: 'agent',
      groupId: timingPhaseOf(step.kind, step.step),
      subEntries: subEntries.length > 0 ? subEntries : undefined,
      badges,
    });
    if (subEntries.length > 0) defaultExpandedIds.push(step.id);
    workEntryCount += 1;
  }

  // Degenerate: anchors existed but no real step landed on the timeline.
  if (workEntryCount === 0) return null;

  // When the run recorded no build-phase step (e.g. the stopped-pool claim path,
  // whose incremental refresh doesn't emit app_up) but the deploy phase ran past
  // the ready steps, synthesize one opaque "Build" bar for that tail — otherwise
  // minutes of build time would silently vanish from the chart. Runs with leaf
  // phase rows record `phase6_build` / `phase7_health` directly and never need
  // the estimate.
  const hasBuildEntry = entries.some((e) => e.groupId === 'build');
  if (leafPhases.length === 0 && !hasBuildEntry && deploy && deployOffset !== null) {
    const deployEnd = deployOffset + deploy.durationMs;
    const readyEnd = entries
      .filter((e) => e.groupId === 'ready')
      .reduce((max, e) => Math.max(max, e.startMs + e.durationMs), deployOffset);
    if (deployEnd - readyEnd > 0) {
      entries.push({
        id: `${deploy.id}-build`,
        label: 'Build',
        startMs: readyEnd,
        durationMs: deployEnd - readyEnd,
        status: 'success',
        tone: 'agent',
        groupId: 'build',
      });
    }
  }

  appendFailedStepEntry(entries, {
    failedStep: options?.failedStep ?? null,
    totalMs: total?.durationMs ?? null,
  });

  const maxEnd = Math.max(...entries.map((e) => e.startMs + e.durationMs), 0);
  const totalDurationMs = Math.max(total?.durationMs ?? 0, maxEnd);

  return { entries, totalDurationMs, defaultExpandedIds, groups: provisionPhaseGroups };
}

/**
 * Append the step provisioning died in as a red bar covering the unaccounted
 * tail.
 *
 * A phase that throws never closes its timer, so it writes no row — which means
 * the failing step is *absent* from the chart, and every bar that IS there
 * genuinely succeeded. Marking an existing bar as failed would therefore blame
 * the wrong step; the honest rendering is a synthetic entry spanning from the
 * last completed work to the end of the run, which is exactly the time spent in
 * the step that died.
 */
function appendFailedStepEntry(
  entries: WaterfallEntry[],
  { failedStep, totalMs }: { failedStep: string | null; totalMs: number | null }
): void {
  if (!failedStep || entries.length === 0) return;

  const completedEnd = Math.max(...entries.map((e) => e.startMs + e.durationMs), 0);
  // Without a `total` there is no run end to measure the tail against, so show a
  // marker bar rather than inventing a duration.
  const durationMs = totalMs !== null ? Math.max(0, totalMs - completedEnd) : 0;

  entries.push({
    id: 'failed-step',
    label: failedStep,
    startMs: completedEnd,
    durationMs,
    status: 'failed',
    tone: 'danger',
    errorMessage: 'Provisioning stopped here — later steps did not run.',
    // The failure is always the last thing attempted, so it belongs to whichever
    // half the run had reached.
    groupId: entries[entries.length - 1]?.groupId ?? 'build',
  });
}
